<template>
    <div class="forward-department">
        <div class="header">
            <a-button @click="$emit('back')" class="back-button">
                <a-icon type="arrow-left" />
            </a-button>
            <h3>{{ $t("message.selectDepartmentToForward") }}</h3>
        </div>
        <div class="explanation">
            {{ $t("message.selectDepartmentToForwardAgent", { selectAgentName: selectedAgentName }) }}
        </div>
        <div class="search-input">
            <a-input-search :placeholder="$t('message.searchDepartment')" v-model="searchText" @search="onSearch" />
        </div>
        <div class="department-list">
            <div class="department-grid">
                <div
                    v-for="department in filteredDepartments"
                    :key="department._id"
                    class="department-item"
                    :class="{
                        'department-item--disabled': !isDepartmentValid(department),
                        'department-item--current': department._id === defaultSelectedDepartment,
                    }"
                >
                    <div class="department-name">
                        {{ getDepartmentName(department) }}
                        <a-badge status="processing" :text="$t('message.currentDepartment')" />
                        <a-badge status="success" :text="$t('message.validDepartment')" />
                    </div>
                    <div class="department-actions">
                        <SupsisButton
                            @click="forwardToDepartment(department._id)"
                            ghost
                            primary
                            :disabled="!isDepartmentValid(department)"
                        >
                            {{ $t("message.changeDepartmentAndForward") }}
                        </SupsisButton>
                    </div>
                    <div class="invalid-reason" v-if="!isDepartmentValid(department)">
                        {{ getInvalidReason(department) }}
                        <a-button type="link" class="settings-link" @click="navigateToSettings(department)">
                            {{ $t("message.goToSettings") }}
                        </a-button>
                    </div>
                    <div v-else class="valid-reason">{{ $t("message.youCanForwardToThisDepartment") }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import _ from "lodash";
import { mapState } from "vuex";

export default Vue.extend({
    name: "ForwardDepartment",
    components: {
        SupsisButton,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        selectedUserId: {
            type: String,
            default: "",
        },
        defaultSelectedDepartment: {
            type: String,
        },
        defaultChangeOwnerShip: {
            type: Boolean,
        },
    },
    data() {
        return {
            searchText: "",
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        departments() {
            return this.$store.state.subject.subjects?.data || [];
        },
        allowedDepartments() {
            return this.$parent.getAllowedDepartments();
        },
        sortedDepartments() {
            const currentDepartmentId = this.contact.lastConversation?.subject;
            const validDepartments = this.departments.filter(
                (dept) => this.isDepartmentValid(dept) && dept._id !== currentDepartmentId
            );
            const invalidDepartments = this.departments.filter((dept) => !this.isDepartmentValid(dept));

            let sortedDepartments = [
                ...(currentDepartmentId ? [this.departments.find((dept) => dept._id === currentDepartmentId)] : []),
                ..._.orderBy(validDepartments, (dept) => dept.languageContent[this.$i18n.locale]?.name || "", ["asc"]),
                ..._.orderBy(invalidDepartments, (dept) => dept.languageContent[this.$i18n.locale]?.name || "", [
                    "asc",
                ]),
            ].filter(Boolean);

            sortedDepartments = this.$_.uniqBy(sortedDepartments, "_id");

            return sortedDepartments;
        },
        filteredDepartments() {
            if (!this.searchText) return this.sortedDepartments;
            const searchText = this.searchText.toLowerCase();
            return this.sortedDepartments.filter((dept) => {
                const departmentName = this.getDepartmentName(dept).toLowerCase();
                return departmentName.includes(searchText);
            });
        },
        selectedAgentName() {
            const selectedAgent = this.$store.state.user.users.data.find((u) => u._id === this.selectedUserId);
            return selectedAgent ? selectedAgent.name : "";
        },
    },
    methods: {
        getDepartmentName(department) {
            const currentLocaleExists = !!department.languageContent[this.$i18n.locale];
            const firstLocale = Object.keys(department.languageContent)[0];
            return currentLocaleExists
                ? department.languageContent[this.$i18n.locale].name
                : department.languageContent[firstLocale].name;
        },
        isDepartmentValid(department) {
            const isChannelMatchedDepartment = this.allowedDepartments.includes(department._id);
            if (!isChannelMatchedDepartment) return false;
            const user = this.$store.state.user.users.data.find((u) => u._id === this.selectedUserId);
            const userDepartments = user?.subjects || [];
            if (userDepartments.length === 0) {
                return true;
            }
            return userDepartments.includes(department._id);
        },
        getInvalidReason(department) {
            const user = this.$store.state.user.users.data.find((u) => u._id === this.selectedUserId);
            if (!this.allowedDepartments.includes(department._id)) {
                return this.$t("message.channelPrivacyPolicyNotMet");
            } else {
                return this.$t("message.userIsNotInDepartment");
            }
        },
        forwardToDepartment(departmentId) {
            this.$emit("forward", departmentId);
        },
        onSearch() {
            // Trigger search
        },
        goToChannelSettings() {
            const _channel = this.contact?.channel;
            const channelId = typeof _channel === "string" ? _channel : _channel._id;
            const channel = this.channels.find((c) => c._id === channelId);
            if (channel) {
                this.$router.push(`/integrations/${channel.type}/${channel._id}/detail/visibility`);
            }
        },
        gotoUsersPage() {
            this.$router.push(`/team`);
        },
        navigateToSettings(department) {
            if (!this.allowedDepartments.includes(department._id)) {
                this.goToChannelSettings();
            } else {
                this.gotoUsersPage();
            }
        },
    },
});
</script>

<style scoped lang="scss">
.forward-department {
    .header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        .back-button {
            margin-right: 16px;
        }

        h3 {
            margin: 0;
        }
    }

    .explanation {
        margin-bottom: 16px;
    }

    .search-input {
        margin-bottom: 16px;
    }

    .department-list {
        max-height: 500px;
        overflow-y: auto;
        padding: 12px;
    }

    .department-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
    }

    .department-item {
        padding: 16px;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        transition: all 0.3s ease;
        background: #fff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border-color: #40a9ff;
        }

        &--disabled {
            background: #fafafa;
            opacity: 0.8;

            &:hover {
                transform: none;
                border-color: #e8e8e8;
                box-shadow: none;
            }
        }

        &--current {
            border: 2px solid #1890ff;
            background: #e6f7ff;

            &:hover {
                border-color: #40a9ff;
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
        }
    }

    .department-name {
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .invalid-reason {
        color: #f5222d;
        margin-top: 12px;
        padding: 8px;
        background: #fff1f0;
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .settings-link {
            padding: 0;
            height: auto;
            font-size: 14px;
        }
    }

    .valid-reason {
        color: #52c41a;
        margin-top: 12px;
        padding: 8px;
        background: #f6ffed;
        border-radius: 4px;
    }

    .department-actions {
        text-align: right;
        margin-top: 12px;
    }
}
</style>
